// frontend/src/components/PseDashboard.js
//
// "PSE View" — a Pre-Sales member's own comp, and nothing else (the Pre-Sales
// equivalent of the Sales rep's My Dashboard). Which plan to show comes from the
// user's leadership plan history on /api/auth/me, as of the selected quarter:
//   solutions_architect → Senior Solutions Architect plan (Michael)
//   presales_director   → Director of Presales Engineering plan (Ken)
// Published parameters are read-only here; editing stays on the department
// dashboard for global admins.
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../App';
import { getLeadershipPlan } from '../api/client';
import { quarterOptionsFrom, currentQuarter, Notice } from './ui';
import { DirectorCard, ArchitectCard } from './PresalesDashboard';

const PLAN_TITLE = { solutions_architect: 'Senior Solutions Architect plan', presales_director: 'Director of Presales Engineering plan' };

// The user's leadership plan as of the last day of a quarter.
function planForQuarter(history = [], quarter) {
  const [y, q] = quarter.split('-Q').map(Number);
  const end = `${y}-${String(q * 3).padStart(2, '0')}-${q * 3 === 12 ? 31 : new Date(Date.UTC(y, q * 3, 0)).getUTCDate()}`;
  let best = null;
  for (const a of history) {
    const ef = String(a.effectiveFrom).slice(0, 10);
    if (ef <= end && (!best || ef > String(best.effectiveFrom).slice(0, 10))) best = a;
  }
  return best?.plan || 'none';
}

// viewer: a finance / cross-department user opening the PSE View from the Pre-Sales
// lens — always the Senior Solutions Architect plan (its payee is named on the page).
export default function PseDashboard({ viewer = false } = {}) {
  const { user } = useAuth();
  const options = quarterOptionsFrom('2026-Q4');
  const [quarter, setQuarter] = useState(() => options.includes(currentQuarter()) ? currentQuarter() : options[0]);
  const plan = viewer ? 'solutions_architect' : planForQuarter(user?.leadership_plans || [], quarter);
  const hasPlan = plan === 'solutions_architect' || plan === 'presales_director';
  const api = useApi(() => hasPlan ? getLeadershipPlan(plan, quarter) : Promise.resolve(null), [plan, quarter]);

  return (
    <div className="page-enter page-wrap" style={{ maxWidth: '1200px' }}>
      <div className="page-header">
        <div>
          <div className="label" style={{ marginBottom: '6px' }}>{quarter} · {viewer ? 'Pre-Sales · ' : ''}PSE View{hasPlan ? ` · ${PLAN_TITLE[plan]}` : ''}</div>
          <h1 style={{ fontSize: '1.6rem' }}>{viewer ? `PSE View${api.data?.payees?.length ? ` — ${api.data.payees.map(p => p.name).join(', ')}` : ''}` : 'My Earnings'}</h1>
        </div>
        <select value={quarter} onChange={e => setQuarter(e.target.value)} className="input" style={{ width: 'auto', minWidth: '130px' }}>
          {options.map(q => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>

      {!hasPlan ? (
        <div className="card" style={{ padding: '24px' }}>
          <h3 style={{ marginBottom: '6px' }}>No comp plan assigned for {quarter}</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: 1.6 }}>
            Your Pre-Sales plan has not been assigned to your account yet. Finance sets it on Finance → Users under
            "leadership plan". Once it is, this page shows your quarter: the components, your attainment, and the projected payout.
          </p>
        </div>
      ) : api.loading && !api.data ? (
        <div style={{ padding: '48px', textAlign: 'center' }}><div className="loader-ring" style={{ margin: '0 auto' }} /></div>
      ) : api.error && !api.data ? (
        <Notice tone="red">{String(api.error)}</Notice>
      ) : api.data ? (
        plan === 'solutions_architect'
          ? <ArchitectCard data={api.data} quarter={quarter} refetch={api.refetch} payee={!viewer} />
          : <DirectorCard data={api.data} quarter={quarter} refetch={api.refetch} payee={!viewer} />
      ) : null}

      <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px', lineHeight: 1.5 }}>
        Figures are projected from quarter-to-date closed-won results and update with each sync. Published parameters are
        set before the quarter opens; Finance reviews the calculation and the CEO approves it before payment.
      </p>
    </div>
  );
}
